import has from 'lodash/has';
import every from 'lodash/every';
import { getObjValue, setObjValue, isObjEmpty } from './common-utils';

type FieldValidatorsType = ((...args: any[]) => string)[];
type ValidatorMapType = {
  [key: string]: FieldValidatorsType | ValidatorMapType;
};
export type ErrorType = {
  [key: string]: string | ErrorType;
};
export type UnionValidatorMapList = {
  fieldset: string[];
  validators: FieldValidatorsType;
  errorFields: string[]; // which field to contain the error message
}[];

class FormValidator {
  protected validatorMap: ValidatorMapType = {};
  protected unionValidatorMapList: UnionValidatorMapList = [];
  protected error: ErrorType = {};

  constructor(
    validatorMap: ValidatorMapType = {},
    unionValidatorMapList: UnionValidatorMapList = [],
  ) {
    this.validatorMap = validatorMap;
    this.unionValidatorMapList = unionValidatorMapList;
  }

  getError() {
    return this.error;
  }

  private _validate(
    key: string,
    value: any,
    validators: FieldValidatorsType | ValidatorMapType,
  ): ErrorType {
    const error: ErrorType = {};
    if (!validators) return error;
    if (Array.isArray(validators)) {
      let errorMsg = '';
      validators.forEach((validator) => {
        errorMsg = errorMsg || validator(value);
      });
      error[key] = errorMsg;
    } else {
      const subError: ErrorType = {};
      Object.keys(value).forEach((vKey) => {
        const vVal = value[vKey];
        subError[vKey] = this._validate(vKey, vVal, validators?.[vKey])[vKey];
      });
      error[key] = subError;
    }
    return error;
  }

  unionValidate(values: Record<string, any>) {
    this.unionValidatorMapList.forEach((unionValidatorMap) => {
      const { fieldset, validators, errorFields } = unionValidatorMap;
      let errorMsg = '';
      let checked = false;
      const valueList = fieldset.map((field) => getObjValue(values, field));
      if (every(fieldset, (field) => has(values, field))) {
        validators.forEach((validator) => {
          errorMsg = errorMsg || validator(...valueList);
        });
        errorFields.forEach((errorField) => {
          const err = this.error[errorField];
          !err && setObjValue(this.error, errorField, errorMsg);
        });
        checked = true;
      }
      errorFields.forEach((errorField) => {
        if (checked) {
          const err = this.error[errorField];
          !err && setObjValue(this.error, errorField, errorMsg);
        }
      });
    });
  }

  singleValidate(values: Record<string, any>) {
    const error: ErrorType = {};
    Object.keys(values).forEach((vKey) => {
      const vVal = values[vKey];
      error[vKey] = this._validate(vKey, vVal, this.validatorMap[vKey])[vKey];
    });
    this.error = error;
  }

  validate(values: Record<string, any>) {
    this.singleValidate(values);
    this.unionValidate(values);
  }

  // check if the form data pass the validation
  isOk() {
    return isObjEmpty(this.error);
  }
}

export default FormValidator;
